import { useToast } from "@chakra-ui/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useCreateAiInterviewerMutation } from "../../../graphql";
import { useIsScreenOrgAtsConnected } from "../../../hooks/useIsScreenOrgAtsConnected";
import { useIsScreenPositionRequired } from "../../../hooks/useIsScreenPositionRequired";
import { AiInterviewerWizardContext } from "../AiInterviewerWizardContext";
import { AiInterviewerSetupSubStepFlex } from "./AiInterviewerSetupSubStepFlex";
import { FlexIntakeJdSubStep } from "./FlexIntakeJdSubStep";
import {
  type FlexIntakeFormValues,
  type MaterialInput,
  FLEX_INTAKE_DEFAULT_VALUES,
  isMaterialInputComplete,
  toCreateAiInterviewerVars,
} from "./types";

/**
 * Create view for the flex intake flow (ai_interviewer:flex_intake).
 * Uses the same two sub-steps as the standard flow, but navigates to
 * /train after create instead of the guide tab.
 */
const AiInterviewerCreateViewFlexIntake: React.FC = () => {
  const { setWizardOn, setReadOnly, setSubStepDisplay } = useContext(
    AiInterviewerWizardContext
  );

  const [subStep, setSubStep] = useState<0 | 1>(0);
  const isAtsLess = !useIsScreenOrgAtsConnected();
  const isPositionRequired = useIsScreenPositionRequired();

  useEffect(() => {
    setWizardOn?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  const toast = useToast();

  const [createAiInterviewer, { loading }] = useCreateAiInterviewerMutation({
    onCompleted: (data) => {
      const newId = data?.createAiInterviewer?.aiInterviewer?.id;
      if (newId) {
        setReadOnly?.(false);
        navigate(`/ai-interviewer/${newId}/train`);
      }
    },
    onError: () => {
      toast({
        title: "Error creating AI interviewer",
        description: "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const methods = useForm<FlexIntakeFormValues>({
    defaultValues: FLEX_INTAKE_DEFAULT_VALUES,
    mode: "onTouched",
  });

  const jd = methods.watch("jobDescription");
  const jdValid = isMaterialInputComplete(jd);

  const handleJdChange = useCallback(
    (state: MaterialInput) => {
      methods.setValue("jobDescription", state);
    },
    [methods]
  );

  const onSubmit: SubmitHandler<FlexIntakeFormValues> = useCallback(
    (values) => {
      createAiInterviewer({ variables: toCreateAiInterviewerVars(values) });
    },
    [createAiInterviewer]
  );

  const validateAndAdvance = useCallback(async () => {
    const fieldsToValidate: (keyof FlexIntakeFormValues)[] = [
      "name",
      "displayName",
    ];
    if (isPositionRequired) {
      fieldsToValidate.push("positionId");
    }
    const valid = await methods.trigger(fieldsToValidate);
    if (valid) {
      setSubStep(1);
    }
  }, [methods, isPositionRequired]);

  const submitFromWizard = useCallback(() => {
    methods.handleSubmit(onSubmit)();
  }, [methods, onSubmit]);

  const subStepDisplayValue = useMemo(
    () =>
      subStep === 0
        ? {
            header: "Set up AI interviewer",
            description: "Configure your AI interviewer and interview guide.",
            nextLabel: "Next: Job description",
            onNext: validateAndAdvance,
          }
        : {
            header: "Enter job description",
            description:
              "Add a JD to tailor your interview guide and suggestions.",
            nextLabel: "Next: Train interviewer",
            onNext: submitFromWizard,
            onBack: () => setSubStep(0),
          },
    [subStep, validateAndAdvance, submitFromWizard]
  );

  useEffect(() => {
    setSubStepDisplay?.(subStepDisplayValue);
    return () => setSubStepDisplay?.(null);
  }, [subStepDisplayValue, setSubStepDisplay]);

  useEffect(() => {
    setReadOnly?.(loading);
  }, [loading, setReadOnly]);

  if (subStep === 0) {
    return (
      <AiInterviewerSetupSubStepFlex
        methods={methods}
        onNext={validateAndAdvance}
        onCancel={() => navigate("/ai-interviewers")}
      />
    );
  }

  return (
    <FlexIntakeJdSubStep
      onBack={() => setSubStep(0)}
      onSubmit={submitFromWizard}
      value={jd}
      onChange={handleJdChange}
      isValid={jdValid}
      loading={loading}
      isAtsConnected={!isAtsLess}
    />
  );
};

export { AiInterviewerCreateViewFlexIntake };
